import { parsePhoneNumber } from 'awesome-phonenumber';

import {
  EnumUserRelationshipsSource,
  EnumUserRelationshipsType,
  type User,
} from '../../../lib/graphql';

/**
 * Validates a phone number using awesome-phonenumber library
 * @param phoneNumber - Phone number string to validate
 * @param options - Optional configuration
 * @param options.allowEmpty - If true, returns true for null/undefined values (default: false)
 * @returns true if the phone number is valid, false otherwise
 */
export function isPhoneNumberValid(
  phoneNumber: string | null | undefined,
  options?: { allowEmpty?: boolean },
): boolean {
  if (!phoneNumber) {
    return options?.allowEmpty ?? false;
  }
  try {
    const phone = parsePhoneNumber(phoneNumber, { regionCode: 'US' });
    return phone.valid;
  } catch {
    return false;
  }
}

/**
 * Formats a phone number string for display
 * @param value - Raw phone number (digits only or with formatting), can be null/undefined
 * @returns Formatted phone number like (555) 123-4567, or '-' if no value provided
 */
export function formatPhoneDisplay(value: string | null | undefined): string {
  if (!value) return '-';

  try {
    // Parse the phone number using awesome-phonenumber to handle country codes
    const phone = parsePhoneNumber(value, { regionCode: 'US' });

    // If valid, format as national number (without country code)
    if (phone.valid) {
      // Get national number (e.g., "(555) 123-4567")
      return phone.number?.national || '-';
    }
  } catch {
    // Fall through to manual formatting if parsing fails
  }

  // Fallback: Manual formatting for invalid/unparseable numbers
  const digits = value.replace(/\D/g, '');

  // Remove leading '1' if present (US country code)
  const cleanDigits =
    digits.startsWith('1') && digits.length === 11 ? digits.slice(1) : digits;

  if (cleanDigits.length === 0) return '-';
  if (cleanDigits.length <= 3) return cleanDigits;
  if (cleanDigits.length <= 6)
    return `(${cleanDigits.slice(0, 3)}) ${cleanDigits.slice(3)}`;
  return `(${cleanDigits.slice(0, 3)}) ${cleanDigits.slice(3, 6)}-${cleanDigits.slice(6, 10)}`;
}

/**
 * Extracts only digits from a phone number string
 * @param value - Phone number with any formatting
 * @returns Digits only, max 10 characters
 */
export function extractPhoneDigits(value: string): string {
  return value.replace(/\D/g, '').slice(0, 10);
}

/** The customer fields a display name is resolved from. */
export interface CustomerDisplayNameSource {
  first_name?: string | null;
  last_name?: string | null;
  company?: string | null;
  email?: string | null;
  phone?: string | null;
}

/** Which field a customer's display name came from. */
export type CustomerDisplayNameField =
  | 'name'
  | 'company'
  | 'email'
  | 'phone'
  | 'none';

export const UNNAMED_CUSTOMER_LABEL = 'Unnamed customer';

/**
 * The display-name rule for a single customer wherever one is shown as a
 * title or a link: the person's name, then the company, then the email, then
 * the phone, then "Unnamed customer". Every field is trimmed first, so a
 * whitespace-only name falls through instead of rendering blank, and the
 * result is never empty — a link built from it always has text to click.
 *
 * Distinct from `formatCustomerLabel`, which leads with the company and
 * parenthesises the person: that one disambiguates rows in a picker, this one
 * names a record.
 * @param customer The record to name; a missing record resolves to the last resort.
 * @returns The name, and the field it came from so a caller can avoid showing
 *   that same field a second time beside it.
 */
export function resolveCustomerDisplayName(
  customer: CustomerDisplayNameSource | null | undefined,
): { name: string; field: CustomerDisplayNameField } {
  const personName = [customer?.first_name, customer?.last_name]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');
  if (personName) return { name: personName, field: 'name' };

  const company = customer?.company?.trim();
  if (company) return { name: company, field: 'company' };

  const email = customer?.email?.trim();
  if (email) return { name: email, field: 'email' };

  // `formatPhoneDisplay` answers '-' for a value with no digits in it.
  const phone = customer?.phone?.trim()
    ? formatPhoneDisplay(customer.phone)
    : '-';
  if (phone !== '-') return { name: phone, field: 'phone' };

  return { name: UNNAMED_CUSTOMER_LABEL, field: 'none' };
}

/**
 * `resolveCustomerDisplayName` for callers that only need the text.
 * @param customer The record to name.
 * @returns A non-empty display name.
 */
export function formatCustomerDisplayName(
  customer: CustomerDisplayNameSource | null | undefined,
): string {
  return resolveCustomerDisplayName(customer).name;
}

export function formatUserRelationshipType(
  user: User | undefined | null,
  vendorId?: string,
): string {
  if (!user) return '-';
  const matches = user.relationships?.filter(
    (relationship) => relationship?.vendorId === vendorId,
  );
  const types = (matches ?? [])
    .map((m) => m?.type)
    .filter((t): t is EnumUserRelationshipsType => t != null);

  if (types.length === 1) {
    const t = types[0];
    return t === EnumUserRelationshipsType.Buyer ? 'Buyer' : 'Producer';
  }
  if (types.length > 1) {
    const hasBuyer = types.includes(EnumUserRelationshipsType.Buyer);
    const hasProducer = types.includes(EnumUserRelationshipsType.Producer);
    if (hasBuyer && hasProducer) return 'Buyer & Producer';
    if (hasBuyer) return 'Buyer';
    if (hasProducer) return 'Producer';
  }
  return '-';
}

const RELATIONSHIP_BADGE_COLORS: Record<string, { bg: string; text: string }> =
  {
    Producer: { bg: '#C8E6C9', text: '#2E7D32' },
    Buyer: { bg: '#E3F2FD', text: '#1976D2' },
    'Buyer & Producer': { bg: '#F3E5F5', text: '#7B1FA2' },
  };

const FALLBACK_RELATIONSHIP_COLORS = { bg: '#F5F5F5', text: '#616161' };

export const getRelationshipBadgeColors = (relationship: string) =>
  RELATIONSHIP_BADGE_COLORS[relationship] ?? FALLBACK_RELATIONSHIP_COLORS;

export function formatUserRelationshipSource(
  source: EnumUserRelationshipsSource | null | undefined,
): string | null {
  switch (source) {
    case EnumUserRelationshipsSource.Processor:
      return 'Added by processor';
    case EnumUserRelationshipsSource.LandingPage:
      return 'Landing page';
    case EnumUserRelationshipsSource.BulkUpload:
      return 'Bulk upload';
    default:
      return null;
  }
}
